import { useLayoutEffect, useRef, useState } from 'react';

/**
 * The time stamp printed on the pinned-moment line of a trace chart.
 *
 * Shared by every trace surface — the Dashboard's Cognitive trace (participant
 * and tenant-admin) and the Analysis overlay's focus + hand-motion charts — so
 * the one vertical line those charts still draw (TRACE-NO-MARKER-BARS) reads
 * identically everywhere: same size, weight and placement, coloured to match
 * the line it labels.
 *
 * `text` is pre-formatted by the caller because the axes differ: the Dashboard
 * trace runs on session-elapsed time (`5:42`) while the Analysis overlay runs
 * on the shared wall clock across participants (`18:53`). Each chart stamps its
 * marker in the units its own x-axis is showing.
 *
 * Drawn just INSIDE the top of the plot, on a halo in the card colour so it
 * reads over the lines. It used to sit above the plot, in a top margin only
 * 4–8 px tall, which cut the top of the digits off; and centred on the line it
 * lost half its width at either end of the trace. After the first paint it
 * measures itself and slides sideways to stay inside the chart.
 *
 * Recharts clones this into `<ReferenceLine label={...}>` and injects `viewBox`
 * with the line's pixel box; without it there is nothing to position against,
 * so we render nothing rather than guess.
 */

/** Baseline offset below the plot's top edge: an 11 px label's cap height
 *  plus a little air. */
const INSET_PX = 12;
/** Gap kept between the label and the chart's left / right edge. */
const EDGE_PAD_PX = 2;

export function PinnedMomentLabel(props: {
  viewBox?: { x?: number; y?: number };
  text: string;
  color: string;
  /** Halo behind the digits; pass the card background. */
  halo?: string;
}) {
  const { viewBox, text, color, halo = '#ffffff' } = props;
  const ref = useRef<SVGTextElement>(null);
  const [dx, setDx] = useState(0);
  const x = viewBox?.x;

  useLayoutEffect(() => {
    const el = ref.current;
    const svg = el?.ownerSVGElement;
    // jsdom has no layout (no getBBox); stay centred there.
    if (!el || !svg || typeof el.getBBox !== 'function' || x === undefined) return;
    const half = el.getBBox().width / 2;
    const width = svg.clientWidth || svg.getBoundingClientRect().width;
    let shift = 0;
    if (x - half < EDGE_PAD_PX) shift = EDGE_PAD_PX - (x - half);
    else if (width > 0 && x + half > width - EDGE_PAD_PX) {
      shift = width - EDGE_PAD_PX - (x + half);
    }
    setDx((prev) => (Math.abs(prev - shift) < 0.5 ? prev : shift));
  }, [x, text]);

  if (!viewBox || viewBox.x === undefined || viewBox.y === undefined) {
    return null;
  }
  return (
    <text
      ref={ref}
      x={viewBox.x + dx}
      y={viewBox.y + INSET_PX}
      fontSize={11}
      fontWeight={700}
      fill={color}
      stroke={halo}
      strokeWidth={3}
      paintOrder="stroke"
      textAnchor="middle"
      style={{ pointerEvents: 'none' }}
    >
      {text}
    </text>
  );
}
